import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Shield, Play, Clock, AlertTriangle, CheckCircle, FileText,
  Users, Loader2, Sparkles, Download, BarChart3, Activity,
  FolderOpen, ArrowRight, XCircle, Eye, UserCheck, Bell, Send, Mail, Settings
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeAuditPage = () => {
  const { token } = useAuth();
  const [audit, setAudit] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [generatingRecs, setGeneratingRecs] = useState(false);
  const [recommendations, setRecommendations] = useState('');
  const [selectedGap, setSelectedGap] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [schedule, setSchedule] = useState({ enabled: false, frequency: 'weekly', alert_enabled: true, alert_threshold: 60, alert_emails: [] });
  const [emailInput, setEmailInput] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);

  useEffect(() => { fetchLatestAudit(); fetchHistory(); fetchUsers(); fetchSchedule(); fetchAlertHistory(); }, []);

  const fetchLatestAudit = async () => {
    try {
      const res = await axios.get(`${API}/knowledge-audit/latest`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.audit_id) {
        setAudit(res.data);
        if (res.data.ai_recommendations) setRecommendations(res.data.ai_recommendations);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/knowledge-audit/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(res.data);
    } catch { /* silent */ }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch { /* silent */ }
  };

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API}/knowledge-audit/schedule`, { headers: { Authorization: `Bearer ${token}` } });
      setSchedule(res.data);
    } catch { /* silent */ }
  };

  const fetchAlertHistory = async () => {
    try {
      const res = await axios.get(`${API}/knowledge-audit/alerts`, { headers: { Authorization: `Bearer ${token}` } });
      setAlertHistory(res.data);
    } catch { /* silent */ }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await axios.post(`${API}/knowledge-audit/schedule`, schedule, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Schedule & alert settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingSchedule(false); }
  };

  const testAlert = async () => {
    setTestingAlert(true);
    try {
      const res = await axios.post(`${API}/knowledge-audit/test-alert`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAlertHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send test alert'); }
    finally { setTestingAlert(false); }
  };

  const runScheduledAudit = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/knowledge-audit/run-scheduled`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAudit(res.data);
      setRecommendations('');
      if (res.data.alert_sent) {
        toast.success(`Audit complete & alert sent to ${res.data.alert_recipients?.join(', ')}`);
      } else {
        toast.success(`Audit complete: ${res.data.summary?.total_assets} assets, ${res.data.summary?.total_gaps} gaps`);
      }
      fetchHistory();
      fetchAlertHistory();
    } catch { toast.error('Audit failed'); }
    finally { setRunning(false); }
  };

  const addAlertEmail = () => {
    if (emailInput.trim() && !schedule.alert_emails.includes(emailInput.trim())) {
      setSchedule({ ...schedule, alert_emails: [...schedule.alert_emails, emailInput.trim()] });
      setEmailInput('');
    }
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/knowledge-audit/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAudit(res.data);
      setRecommendations('');
      toast.success(`Audit complete: ${res.data.summary?.total_assets} assets scanned, ${res.data.summary?.total_gaps} gaps found`);
      fetchHistory();
    } catch { toast.error('Audit failed'); }
    finally { setRunning(false); }
  };

  const generateRecs = async () => {
    if (!audit) return;
    setGeneratingRecs(true);
    try {
      const res = await axios.post(`${API}/knowledge-audit/${audit.audit_id}/ai-recommendations`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRecommendations(res.data.recommendations);
      toast.success('AI recommendations generated');
    } catch { toast.error('Failed to generate recommendations'); }
    finally { setGeneratingRecs(false); }
  };

  const handleGapAction = async (gapId, action, ownerId = '') => {
    if (!audit) return;
    try {
      await axios.post(`${API}/knowledge-audit/${audit.audit_id}/gaps/${gapId}/action`,
        { action, owner_id: ownerId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Gap ${action}d`);
      setSelectedGap(null);
      fetchLatestAudit();
    } catch { toast.error('Failed'); }
  };

  const handleExport = async (fmt) => {
    if (!audit) return;
    try {
      const res = await axios.get(`${API}/knowledge-audit/${audit.audit_id}/export?fmt=${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const mimeTypes = { pdf: 'application/pdf', csv: 'text/csv', json: 'application/json' };
      const blob = new Blob([res.data], { type: mimeTypes[fmt] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `knowledge-audit-${audit.audit_id}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#34C759]';
    if (score >= 60) return 'text-[#FF9500]';
    return 'text-[#FF3B30]';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-[#34C759]';
    if (score >= 60) return 'bg-[#FF9500]';
    return 'bg-[#FF3B30]';
  };

  const getSeverityBadge = (sev) => {
    switch (sev) {
      case 'critical': return 'bg-[#FF3B30] text-white';
      case 'high': return 'bg-[#FF9500] text-white';
      case 'medium': return 'bg-[#FFCC00] text-[#0A0A0A]';
      default: return 'bg-[#E5E7EB] text-[#4B5563]';
    }
  };

  return (
    <div data-testid="knowledge-audit-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Knowledge Audit</h1>
          <p className="text-[#4B5563] text-sm">Systematic assessment of your knowledge assets</p>
        </div>
        <div className="flex gap-2">
          {audit && (
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
              <Button size="sm" variant="outline" onClick={() => handleExport('json')} data-testid="export-json-btn"><Download className="w-3.5 h-3.5 mr-1" />JSON</Button>
              <Button size="sm" variant="outline" onClick={() => handleExport('csv')} data-testid="export-csv-btn"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
            </div>
          )}
          <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={runScheduledAudit} disabled={running} data-testid="run-audit-btn">
            {running ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Scanning...</> : <><Play className="w-4 h-4 mr-2" />Run Audit</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
      ) : !audit ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="text-center py-16">
            <Shield className="w-16 h-16 mx-auto mb-4 text-[#E5E7EB]" />
            <h2 className="text-lg font-semibold mb-2">No Audit Data</h2>
            <p className="text-[#9CA3AF] mb-4">Run your first knowledge audit to assess the health of your knowledge base.</p>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={runScheduledAudit} disabled={running}>
              {running ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Scanning...</> : <><Play className="w-4 h-4 mr-2" />Run First Audit</>}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Health Score Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="health-scores">
            {[
              { label: 'Overall', score: audit.health_scores?.overall, icon: Shield },
              { label: 'Inventory', score: audit.health_scores?.inventory, icon: FileText },
              { label: 'Location', score: audit.health_scores?.location, icon: FolderOpen },
              { label: 'Flow', score: audit.health_scores?.flow, icon: Activity },
              { label: 'Gaps', score: audit.health_scores?.gaps, icon: AlertTriangle },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <Card key={s.label} className="border-[#E5E7EB]" data-testid={`score-${s.label.toLowerCase()}`}>
                  <CardContent className="p-4 text-center">
                    <Icon className={`w-5 h-5 mx-auto mb-2 ${getScoreColor(s.score || 0)}`} />
                    <p className={`text-2xl font-bold ${getScoreColor(s.score || 0)}`}>{s.score || 0}%</p>
                    <p className="text-xs text-[#9CA3AF]">{s.label}</p>
                    <div className="w-full h-1.5 bg-[#E5E7EB] rounded-full mt-2 overflow-hidden">
                      <div className={`h-full rounded-full ${getScoreBg(s.score || 0)}`} style={{ width: `${s.score || 0}%` }} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Summary Bar */}
          <div className="flex gap-4 mb-6 text-sm text-[#4B5563]">
            <span className="flex items-center gap-1"><FileText className="w-4 h-4" /><strong>{audit.summary?.total_assets}</strong> total assets</span>
            <span className="flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-[#FF3B30]" /><strong>{audit.summary?.total_gaps}</strong> gaps</span>
            <span className="flex items-center gap-1"><XCircle className="w-4 h-4 text-[#FF3B30]" /><strong>{audit.summary?.critical_gaps}</strong> critical</span>
            <span className="text-[#9CA3AF]">Last run: {audit.run_at ? format(new Date(audit.run_at), 'MMM d, yyyy h:mm a') : 'N/A'}</span>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="gaps" data-testid="tab-gaps">Gaps ({audit.gaps?.length || 0})</TabsTrigger>
              <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
              <TabsTrigger value="recommendations" data-testid="tab-recommendations">AI Insights</TabsTrigger>
              <TabsTrigger value="alerts" data-testid="tab-alerts">
                <Bell className="w-3.5 h-3.5 mr-1" />Alerts
              </TabsTrigger>
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" />Asset Distribution</CardTitle></CardHeader>
                  <CardContent>
                    {Object.entries(audit.inventory?.by_type || {}).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
                        <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                            <div className="h-full bg-[#002FA7] rounded-full" style={{ width: `${(count / audit.inventory.total) * 100}%` }} />
                          </div>
                          <span className="text-sm font-medium w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><FolderOpen className="w-4 h-4" />Storage Locations</CardTitle></CardHeader>
                  <CardContent>
                    {Object.entries(audit.locations || {}).map(([loc, stats]) => (
                      <div key={loc} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
                        <span className="text-sm capitalize">{loc.replace('_', ' ')}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-[#9CA3AF]">{stats.contributors} contributors</span>
                          <Badge variant="secondary">{stats.count}</Badge>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" />Top Contributors</CardTitle></CardHeader>
                  <CardContent>
                    {(audit.flow?.top_contributors || []).slice(0, 5).map((c, i) => (
                      <div key={c.user_id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
                        <span className="text-sm">#{i + 1} {c.user_id}</span>
                        <Badge variant="secondary">{c.count} assets</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF3B30]" />Risk Summary</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between"><span className="text-sm">Stale Assets</span><Badge className="bg-[#FF9500] text-white">{audit.inventory?.stale_count || 0}</Badge></div>
                      <div className="flex justify-between"><span className="text-sm">Orphaned Assets</span><Badge className="bg-[#FFCC00] text-[#0A0A0A]">{audit.inventory?.orphaned_count || 0}</Badge></div>
                      <div className="flex justify-between"><span className="text-sm">Empty Assets</span><Badge variant="secondary">{audit.inventory?.empty_count || 0}</Badge></div>
                      <div className="flex justify-between"><span className="text-sm">Single Points of Failure</span><Badge className="bg-[#FF3B30] text-white">{audit.flow?.single_points_of_failure?.length || 0}</Badge></div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Audit History */}
              {history.length > 1 && (
                <Card className="border-[#E5E7EB] mt-4">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" />Audit History</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {history.map((h) => (
                        <div key={h.audit_id} className="flex items-center justify-between p-2 rounded hover:bg-[#F9FAFB]">
                          <div className="flex items-center gap-3">
                            <span className="text-sm">{format(new Date(h.run_at), 'MMM d, yyyy h:mm a')}</span>
                            <Badge variant="secondary">{h.summary?.total_assets} assets</Badge>
                            <Badge className={h.summary?.critical_gaps > 0 ? 'bg-[#FF3B30] text-white' : 'bg-[#34C759] text-white'}>{h.summary?.total_gaps} gaps</Badge>
                          </div>
                          <span className={`text-lg font-bold ${getScoreColor(h.health_scores?.overall || 0)}`}>{h.health_scores?.overall || 0}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Gaps Tab */}
            <TabsContent value="gaps">
              <Card className="border-[#E5E7EB]">
                <CardContent className="p-0">
                  {!audit.gaps?.length ? (
                    <div className="text-center py-12 text-[#9CA3AF]"><CheckCircle className="w-10 h-10 mx-auto mb-3 text-[#34C759]" /><p>No gaps found!</p></div>
                  ) : (
                    <div className="divide-y divide-[#E5E7EB]">
                      {audit.gaps.filter(g => g.status !== 'suppressed').map((g) => (
                        <div key={g.gap_id} className={`p-4 ${g.status === 'resolved' ? 'opacity-50' : ''}`} data-testid={`gap-${g.gap_id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={getSeverityBadge(g.severity)}>{g.severity}</Badge>
                                <Badge variant="secondary" className="text-[10px]">{g.type.replace('_', ' ')}</Badge>
                                {g.status !== 'open' && <Badge variant="outline" className="text-[10px]">{g.status}</Badge>}
                              </div>
                              <p className="text-sm font-medium">{g.description}</p>
                              <p className="text-xs text-[#9CA3AF] mt-1">{g.recommendation}</p>
                            </div>
                            {g.status === 'open' && (
                              <div className="flex gap-1 flex-shrink-0 ml-2">
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-[#34C759]" onClick={() => handleGapAction(g.gap_id, 'resolve')} data-testid={`resolve-${g.gap_id}`}>
                                  <CheckCircle className="w-3 h-3 mr-1" />Resolve
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => handleGapAction(g.gap_id, 'accept')}>Accept</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-[#9CA3AF]" onClick={() => handleGapAction(g.gap_id, 'suppress')}>Suppress</Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Inventory Tab */}
            <TabsContent value="inventory">
              <Card className="border-[#E5E7EB]">
                <CardHeader>
                  <CardTitle className="text-sm">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                      <p className="text-2xl font-bold text-[#002FA7]">{audit.inventory?.total || 0}</p>
                      <p className="text-xs text-[#9CA3AF]">Total Assets</p>
                    </div>
                    <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                      <p className="text-2xl font-bold text-[#FF9500]">{audit.inventory?.stale_count || 0}</p>
                      <p className="text-xs text-[#9CA3AF]">Stale (9+ months)</p>
                    </div>
                    <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                      <p className="text-2xl font-bold text-[#FF3B30]">{audit.inventory?.orphaned_count || 0}</p>
                      <p className="text-xs text-[#9CA3AF]">No Owner</p>
                    </div>
                    <div className="text-center p-3 bg-[#F3F4F6] rounded-lg">
                      <p className="text-2xl font-bold text-[#8E8E93]">{audit.inventory?.empty_count || 0}</p>
                      <p className="text-xs text-[#9CA3AF]">Empty Content</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* AI Recommendations Tab */}
            <TabsContent value="recommendations">
              <Card className="border-[#E5E7EB]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#002FA7]" />AI-Powered Recommendations</CardTitle>
                    <Button size="sm" onClick={generateRecs} disabled={generatingRecs} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="generate-recs-btn">
                      {generatingRecs ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />{recommendations ? 'Regenerate' : 'Generate'}</>}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {recommendations ? (
                    <div className="prose prose-sm max-w-none text-sm text-[#4B5563] [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-[#0A0A0A] [&_h3]:text-sm [&_h3]:font-semibold [&_strong]:text-[#0A0A0A]" data-testid="ai-recs-content">
                      {recommendations.split('\n').map((line, i) => {
                        if (!line.trim()) return <br key={i} />;
                        if (line.startsWith('### ')) return <h3 key={i}>{line.slice(4)}</h3>;
                        if (line.startsWith('## ')) return <h2 key={i}>{line.slice(3)}</h2>;
                        if (line.startsWith('- ')) return <div key={i} className="flex gap-2 ml-2"><span className="text-[#002FA7]">-</span><span>{line.slice(2)}</span></div>;
                        if (line.match(/^\d+\.\s/)) return <div key={i} className="flex gap-2 ml-2"><span className="text-[#002FA7] font-medium">{line.match(/^\d+/)[0]}.</span><span>{line.replace(/^\d+\.\s/, '')}</span></div>;
                        return <p key={i}>{line}</p>;
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-[#9CA3AF] py-8">Click "Generate" for AI-powered knowledge management recommendations based on audit data.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Alert Configuration */}
                <Card className="border-[#E5E7EB]" data-testid="alert-config-card">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-[#FF3B30]" />Alert Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={schedule.enabled} onCheckedChange={(v) => setSchedule({ ...schedule, enabled: v })} data-testid="schedule-enabled" />
                      <Label className="text-sm">Enable scheduled audits</Label>
                    </div>
                    {schedule.enabled && (
                      <div className="pl-7">
                        <Label className="text-xs text-[#9CA3AF]">Frequency</Label>
                        <Select value={schedule.frequency} onValueChange={(v) => setSchedule({ ...schedule, frequency: v })}>
                          <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="biweekly">Biweekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="border-t border-[#E5E7EB] pt-4">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={schedule.alert_enabled} onCheckedChange={(v) => setSchedule({ ...schedule, alert_enabled: v })} data-testid="alert-enabled" />
                        <Label className="text-sm">Send email alerts when scores drop below threshold</Label>
                      </div>
                    </div>

                    {schedule.alert_enabled && (
                      <div className="pl-7 space-y-3">
                        <div>
                          <Label className="text-xs text-[#9CA3AF]">Alert Threshold (%)</Label>
                          <div className="flex items-center gap-3">
                            <Input type="number" min={0} max={100} value={schedule.alert_threshold} onChange={(e) => setSchedule({ ...schedule, alert_threshold: parseInt(e.target.value) || 60 })} className="w-24" data-testid="alert-threshold" />
                            <span className="text-xs text-[#9CA3AF]">Alert when any score drops below this</span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#9CA3AF]">Alert Recipients</Label>
                          <div className="flex gap-2 mt-1">
                            <Input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="team-lead@company.com" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAlertEmail())} data-testid="alert-email-input" />
                            <Button variant="outline" size="sm" onClick={addAlertEmail}>Add</Button>
                          </div>
                          {schedule.alert_emails?.length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-2">
                              {schedule.alert_emails.map((email, i) => (
                                <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setSchedule({ ...schedule, alert_emails: schedule.alert_emails.filter((_, j) => j !== i) })}>
                                  <Mail className="w-3 h-3 mr-1" />{email} ×
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" onClick={saveSchedule} disabled={savingSchedule} data-testid="save-alert-config-btn">
                        {savingSchedule ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Settings className="w-3.5 h-3.5 mr-1.5" />}
                        Save Settings
                      </Button>
                      {schedule.alert_enabled && schedule.alert_emails?.length > 0 && (
                        <Button size="sm" variant="outline" onClick={testAlert} disabled={testingAlert} data-testid="test-alert-btn">
                          {testingAlert ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                          Send Test Alert
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Alert History */}
                <Card className="border-[#E5E7EB]">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" />Alert History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {alertHistory.length === 0 ? (
                      <p className="text-sm text-[#9CA3AF] text-center py-8">No alerts sent yet</p>
                    ) : (
                      <div className="space-y-2">
                        {alertHistory.map((a) => (
                          <div key={a.alert_id} className="p-3 bg-[#FFF5F5] rounded-lg text-xs" data-testid={`alert-${a.alert_id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-[#FF3B30] flex items-center gap-1"><Bell className="w-3 h-3" />Alert Sent</span>
                              <span className="text-[#9CA3AF]">{format(new Date(a.created_at), 'MMM d, h:mm a')}</span>
                            </div>
                            <p className="text-[#4B5563]">To: {a.recipients?.join(', ')}</p>
                            <div className="flex gap-2 mt-1">
                              {Object.entries(a.scores_below || {}).map(([dim, score]) => (
                                <Badge key={dim} className="bg-[#FF3B30] text-white text-[9px]">{dim}: {score}%</Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default KnowledgeAuditPage;
